import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { useDispatch } from 'src/state/redux';
import { v4 as uuid } from 'uuid';
import { DNAButton, DNAModal, DNAText } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { ToDo, TodoStatus } from '@alucio/aws-beacon-amplify/src/models';
import { EditHubRHForm, getWidgetFormName } from 'src/screens/Hubs/EditHub/useHubForm';
import { ModifiedHubSection } from 'src/state/context/Hubs/HubsStateProvider';
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import * as logger from 'src/utils/logger';
import { HubORM } from 'src/types/orms';
import { HubType } from '@alucio/aws-beacon-amplify/lib/models';

const ACTIVE_TODOS_LIMIT = 50
const ERROR_MESSAGES = {
  MAX_LENGTH: `You have reached the max limit of ${ACTIVE_TODOS_LIMIT} items. Please remove one to continue.`,
}

// Extracted modal component
const ResolutionModal: React.FC<{
  onAddResolution: () => void;
  onMarkAsComplete: () => void;
}> = React.memo(({ onAddResolution, onMarkAsComplete }) => (
  <DNAModal>
    <DNAModal.Header onClose={onMarkAsComplete}>
      Add resolution?
    </DNAModal.Header>
    <DNAModal.Body>
      <DNAText style={{ color: colors['color-text-secondary'] }}>
      {
        'Adding a resolution or description can help your audience better understand\n' +
        'this task and how it was solved.'
      }
      </DNAText>
    </DNAModal.Body>
    <DNAModal.Confirmation>
      <DNAButton
        appearance="outline"
        iconLeft="check-square"
        onPress={onMarkAsComplete}
        children="Mark as complete"
      />
      <DNAButton
        onPress={onAddResolution}
        children="Add resolution"
      />
    </DNAModal.Confirmation>
  </DNAModal>
));

ResolutionModal.displayName = 'ResolutionModal';

interface TodoProviderProps {
  children: ReactNode;
  rhForm: EditHubRHForm;
  widget: ModifiedHubSection;
}
interface TodoContextProps {
  rhForm: EditHubRHForm
  widget: ModifiedHubSection
  items:ToDo[]
  errorMsg: string[]
  setErrorMsg: React.Dispatch<React.SetStateAction<string[]>>
  showCompleted: boolean
  setShowCompleted: React.Dispatch<React.SetStateAction<boolean>>
  activeItems: ToDo[]
  completedItems: ToDo[]
  isNewItem: boolean
  editingId?: string
  setEditingId: React.Dispatch<React.SetStateAction<string | undefined>>
  updateFormValue: (items?: ToDo[]) => void
  addToDo: () => void
  updateItemStatus: (id: string, status: TodoStatus) => void
  onCancelEdit: () => void
  handleOnCompleted: (id: string, isCompleted: boolean, setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>) => void
  hubORM?: HubORM
}

export const TodoContext = createContext<TodoContextProps>(undefined!);

export const TodoProvider: React.FC<TodoProviderProps> = ({ children, rhForm, widget }) => {
  const { hubORM } = useHubsState()
  const dispatch = useDispatch()
  const { setValue, getValues, trigger } = rhForm
  const widgetFormName = getWidgetFormName(widget.id)
  const toDosFormName = `${widgetFormName}.toDos` as const

  const initialItems = useMemo(() => {
    return widget.toDos
      ?.map(toDo => ({ ...toDo, completedAt: toDo.completedAt || undefined }))
      .sort((a, b) => a.order - b.order)
      ?? []
  }, [widget])
  const [items, setItems] = useState<ToDo[]>(initialItems)
  const [errorMsg, setErrorMsg] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | undefined>(undefined)
  const [isNewItem, setIsNewItem] = useState<boolean>(false)
  const [showCompleted, setShowCompleted] = useState<boolean>(false)

  const activeItems = useMemo(() => {
    return items
      .filter(item => item.status === TodoStatus.ACTIVE)
      .sort((a, b) => a.order - b.order)
  }, [items])

  const completedItems = useMemo(() => {
    return items
      .filter(item => item.status === TodoStatus.COMPLETED)
      .sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt) : null
        const dateB = b.completedAt ? new Date(b.completedAt) : null
        if (dateA === null && dateB === null) return 0; // Both dates are undefined, keep their order unchanged
        if (dateA === null) return 1; // dateA is undefined, move it to the end of the array
        if (dateB === null) return -1; // dateB is undefined, move it to the end of the array
        // Compare the dates in descending order (from the most recent to the oldest)
        return dateB.getTime() - dateA.getTime();
      })
  }, [items])

  useEffect(() => {
    if ((activeItems.length + completedItems.length) < ACTIVE_TODOS_LIMIT) {
      setErrorMsg(p => {
        const errMsgIdx = p.findIndex(errMsg => errMsg === ERROR_MESSAGES.MAX_LENGTH)
        if (errMsgIdx !== -1) {
          const errors = [...p]
          errors.splice(errMsgIdx, 1)
          return errors
        }
        else return p
      })
    }
  }, [activeItems])

  // *** Functions ***
  const updateFormValue = useCallback((toDos?: ToDo[]) => {
    const widgetFormValue = getValues(widgetFormName)
    const toDosValue = toDos?.slice() || widgetFormValue?.toDos?.slice()
    if (editingId) {
      const editingToDoIdx = toDosValue?.findIndex(toDo => toDo.id === editingId) ?? -1
      if (toDosValue && editingToDoIdx !== -1) {
        toDosValue.splice(editingToDoIdx, 1, {...toDosValue[editingToDoIdx], updatedAt: new Date().toISOString()})
      }
    }

    if (widgetFormValue) {
      const newValue = {
        ...widgetFormValue,
        toDos: toDosValue,
        updatedAt: new Date().toISOString(),
      }
      setValue(widgetFormName, newValue, { shouldDirty: true })
    }
    setItems(toDosValue || [])
    trigger(toDosFormName).then(isValid => {
      if (isValid) {
        setEditingId(undefined)
        setIsNewItem(false)
      }
    })
  }, [getValues, setValue, trigger, isNewItem, editingId])
  
  const addToDo = useCallback(() => {
    if ((activeItems.length + completedItems.length) >= ACTIVE_TODOS_LIMIT) {
      logger.hub.widgets.todo.warn(`Reached the maximum todo limit ${ACTIVE_TODOS_LIMIT}, Cannot add new todos`)
      setErrorMsg([ERROR_MESSAGES.MAX_LENGTH])
      return
    }
    if (!editingId) {
      const newTodo = {
        id: uuid(),
        title: '',
        resolution: '',
        status: TodoStatus.ACTIVE,
        order: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      const newItems = [newTodo, ...items].map((toDo, index) => ({ ...toDo, order: index }))
      const widgetFormValue = getValues(widgetFormName)
      if(widgetFormValue){
        setValue(widgetFormName, {...widgetFormValue, toDos: newItems}, { shouldDirty: true })
      }
      setItems(newItems)
      setIsNewItem(true)
      setEditingId(newTodo.id)

      analytics?.track('HUB_TASK_ADD', {
        action: 'CREATE',
        category: 'HUB',
        hubId: hubORM?.model.id,
        toDoId: newTodo.id,
      });
      logger.hub.widgets.todo.debug(`Adding new todo item: ${newTodo.id}`)
    }
  }, [items, activeItems, completedItems, editingId, getValues, setValue])

  const updateItemStatus = useCallback((id: string, status: TodoStatus) => {
    logger.hub.widgets.todo.debug(`Updating todo item ${id} status to ${status}`)
    // The items array is sorted in this order: [...completedItems, ...activeItems, ...deletedItems]
    const isCompleted = status === TodoStatus.COMPLETED
    const isActive = status === TodoStatus.ACTIVE
    const newIndex = isCompleted
      ? 0
      : isActive
        ? completedItems.length - 1
        : items.length - 1
    const itemsCopy = [...items]
    const index = items.findIndex((item) => item.id === id)
    // We only want to track TASK_COMPLETE for the first time
    if (isCompleted && !itemsCopy[index].completedAt) {
      analytics?.track('HUB_TASK_COMPLETE', {
        category: 'HUB',
        hubId: hubORM?.model.id,
        toDoId: id,
      })
    }
    itemsCopy[index] = {
      ...items[index],
      status,
      updatedAt: new Date().toISOString(),
      completedAt: isCompleted ? new Date().toISOString() : items[index].completedAt,
    };
    const newItems = arrayMove(itemsCopy, index, newIndex)
    const newItemsWithUpdatedOrder = newItems.map((item, index) => {
      return { ...item, order: index }
    })
    updateFormValue(newItemsWithUpdatedOrder)
  }, [items, completedItems])

  const onCancelEdit = useCallback(() => {
    if (isNewItem) {
      const newItems = items.filter((toDo) => toDo.id !== editingId)
      setIsNewItem(false)
      setEditingId(undefined)
      updateFormValue(newItems)
    }
    else {
      setIsNewItem(false)
      setEditingId(undefined)
      updateFormValue(items)
    }
  }, [items, isNewItem, editingId])

  const handleOnCompleted = useCallback((
    id: string,
    isCompleted: boolean,
    setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (!isCompleted) {
      setIsCompleted(isCompleted)
      updateItemStatus(id, TodoStatus.ACTIVE)
    }
    else {
      const toDoItem = items.find((toDo) => toDo.id === id)
      // the reason we are checking if the template instance section is locked, is because user cant add a resolution to locked sections
      const canAddResolution = !toDoItem?.resolution && (hubORM?.model.hubType !== HubType.TEMPLATE_INSTANCE || !widget.locked)

      if (canAddResolution) {
        const onAddResolution = () => {
          dispatch(DNAModalActions.closeModal())
          setIsCompleted(isCompleted)
          setEditingId(id)
        }
        const onMarkAsComplete = () => {
          dispatch(DNAModalActions.closeModal())
          setIsCompleted(isCompleted)
          updateItemStatus(id, TodoStatus.COMPLETED)
        }
        dispatch(
          DNAModalActions.setModal({
            isVisible: true,
            allowBackdropCancel: true,
            component: () => (
              <ResolutionModal
                onAddResolution={onAddResolution}
                onMarkAsComplete={onMarkAsComplete}
              />
            )
          }));
      }
      else {
        setIsCompleted(isCompleted)
        updateItemStatus(id, TodoStatus.COMPLETED)
      }
    }
  }, [dispatch, items, updateItemStatus])

  return (
    <TodoContext.Provider
      value={
        {
          rhForm,
          widget,
          items,
          errorMsg,
          setErrorMsg,
          showCompleted,
          setShowCompleted,
          activeItems,
          completedItems,
          isNewItem,
          editingId,
          setEditingId,
          updateFormValue,
          addToDo,
          updateItemStatus,
          onCancelEdit,
          handleOnCompleted,
          hubORM,
        }}
    >
      {children}
    </TodoContext.Provider>
  );
};

export const useTodoState = () => {
  const context = useContext(TodoContext)
  if (!context) {
    const errorText = 'useTodoState must be used within the TodoProvider'
    logger.hub.widgets.todo.error(errorText)
    throw new Error(errorText)
  }
  return context;
}
